import React from "react";
import _ from "lodash";

import { getMemoizedLangFunction } from "Utils";
import {
  isStandardsParentTaggingEnabled,
  isATLsParentTaggingEnabled,
  shouldShowFlatListForStandards,
  shouldShowFlatListForAtls,
  isDatasetsParentTaggingEnabled,
} from "Utils/ACLStoreUtils";
import ACLStore from "lib/aclStore";
import {
  DATA_GRID_CHECKBOX_STATES,
  DATA_GRID_COLUMN_TYPES,
  DATA_GRID_CELL_TYPES,
} from "StandardsTable/modules/constants";
import getCheckboxValue from "StandardsTable/modules/cellUtils/getCheckboxValue";
import {
  ELEMENT_TYPE_PYP_ATL,
  ELEMENT_TYPE_MYP_ATL,
  ELEMENT_TYPE_DP_ATL,
  ELEMENT_TYPE_UBD_ATL,
  ELEMENT_TYPE_UBD_SKILL,
  ELEMENT_TYPE_MYP_LEARNING_STANDARD,
  ELEMENT_TYPE_UBD_LEARNING_STANDARD,
  ELEMENT_TYPE_DP_LEARNING_STANDARD,
  ELEMENT_TYPE_PYP_LEARNING_STANDARD,
  ELEMENT_TYPE_MYP_RELATED_CONCEPT,
  ELEMENT_TYPE_MYP_SPECIFIED_CONCEPT,
  ELEMENT_TYPE_MYP_OBJECTIVES,
  ELEMENT_TYPE_DP_SYLLABUS,
  ELEMENT_TYPE_DP_KNOWLEDGE_FRAMEWORK,
  PLANNER_ELEMENT_NESTED_CHECKLIST,
} from "Constants/stringConstants";
import {
  DATASET_V2_PERMISSION,
  ENABLE_NEW_TAGGING_MODAL,
  ENABLE_STANDARDS_V2,
} from "Constants/permissionConstants";

import {
  SHOW_FILTER_TYPES,
  FILTER_TYPES,
  FILTER_TYPE_TO_VARIABLE_NAME,
  DEFAULT_FILTERS,
  STATUS_OPTIONS,
  SHOW_FILTER_OPTIONS,
  FILTERS_TO_SHOW,
  FILTERS_TO_DATA_GRID_COLUMN_ID,
  ACTION_TYPES,
  VARIABLE_NAME_TO_FILTER_TYPE,
  PARENT_TAGGING_SUPPORTED_PLANNER_ELEMENT_TYPES,
} from "./Constants";
import { ACADEMIC_SETUP_TYPES } from "SubjectStandardsV2/modules/stringConstants";

const getLocalizedFilterLabels = getMemoizedLangFunction(({ t }) => ({
  [FILTER_TYPES.GRADES]: t("scopeAndSequence:grade_levels"),
  [FILTER_TYPES.SUBJECT_AREAS]: t("scopeAndSequence:subject_areas"),
  [FILTER_TYPES.TAGS]: t("common:tags"),
  [FILTER_TYPES.STATUS]: t("common:status"),
  [FILTER_TYPES.SHOW]: t("scopeAndSequence:power_standards"),
  [FILTER_TYPES.SUBJECT_GROUP]: t("common:subject_group_plural"),
}));

const getStatusFilterOptions = getMemoizedLangFunction(({ t }) =>
  STATUS_OPTIONS(t)
);

export const getShowFilterOptions = getMemoizedLangFunction(({ t }) =>
  SHOW_FILTER_OPTIONS(t)
);

export const getCurrentFiltersFromFilterKeys = ({ filters }) => {
  const currentFilters = {};

  _.forEach(filters, (filterValue, filterKey) => {
    const varName = FILTER_TYPE_TO_VARIABLE_NAME[filterKey];
    switch (filterKey) {
      case FILTER_TYPES.SHOW: {
        if (filterValue === SHOW_FILTER_TYPES.POWER_STANDARD) {
          currentFilters[varName] = true;
        }
        break;
      }
      default: {
        if (varName) currentFilters[varName] = filterValue;
        else currentFilters[filterKey] = filterValue;
        break;
      }
    }
  });

  return currentFilters;
};

export const getFilterOverlayOptions = ({
  grades,
  subjects,
  tags,
  subjectGroups,
  defaultColumns,
  plannerElementType,
}) => {
  const localizedFilterLabels = getLocalizedFilterLabels();
  const { filtersToBeShown } = getPlannerElementConfigurations({
    plannerElementType,
  });

  return _.chain(FILTERS_TO_SHOW)
    .filter(filter => {
      const correspondingColumn = FILTERS_TO_DATA_GRID_COLUMN_ID[filter];

      if (correspondingColumn) {
        return _.includes(defaultColumns, correspondingColumn);
      } else if (!_.isEmpty(filtersToBeShown)) {
        const filterConfig = _.find(filtersToBeShown, { type: filter });
        if (!filterConfig) return false;
        const { permission } = filterConfig;
        if (permission) return ACLStore.can(permission);
      }

      return false;
    })
    .map(filter => {
      switch (filter) {
        case FILTER_TYPES.GRADES: {
          return {
            id: FILTER_TYPES.GRADES,
            label: localizedFilterLabels[FILTER_TYPES.GRADES],
            optionsType: "checkbox",
            filterOptions: _.map(grades, ({ id, name }) => ({
              id,
              label: name,
            })),
            includeAllOption: true,
            showSearch: true,
          };
        }

        case FILTER_TYPES.SUBJECT_AREAS: {
          return {
            id: FILTER_TYPES.SUBJECT_AREAS,
            label: localizedFilterLabels[FILTER_TYPES.SUBJECT_AREAS],
            optionsType: "checkbox",
            filterOptions: _.map(subjects, ({ id, name }) => ({
              id,
              label: name,
            })),
            includeAllOption: true,
            showSearch: true,
          };
        }

        case FILTER_TYPES.TAGS: {
          return {
            id: FILTER_TYPES.TAGS,
            label: localizedFilterLabels[FILTER_TYPES.TAGS],
            optionsType: "checkbox",
            filterOptions: tags,
            includeAllOption: true,
            showSearch: true,
          };
        }

        case FILTER_TYPES.STATUS: {
          return {
            id: FILTER_TYPES.STATUS,
            label: localizedFilterLabels[FILTER_TYPES.STATUS],
            optionsType: "radio",
            filterOptions: getStatusFilterOptions(),
          };
        }

        case FILTER_TYPES.SHOW: {
          return {
            id: FILTER_TYPES.SHOW,
            label: localizedFilterLabels[FILTER_TYPES.SHOW],
            optionsType: "radio",
            filterOptions: getShowFilterOptions(),
          };
        }
        case FILTER_TYPES.SUBJECT_GROUP: {
          return {
            id: FILTER_TYPES.SUBJECT_GROUP,
            label: localizedFilterLabels[FILTER_TYPES.SUBJECT_GROUP],
            optionsType: "checkbox",
            filterOptions: _.map(subjectGroups, ({ id, name }) => ({
              id,
              label: name,
            })),
            includeAllOption: true,
            showSearch: true,
          };
        }
      }
    })
    .value();
};

export const getAllFiltersCount = ({
  currentFilters,
  filterOverlayOptions,
}) => {
  const defaultFilters = DEFAULT_FILTERS;

  return _.reduce(
    filterOverlayOptions,
    (acc, option) => {
      const { id: type } = option;
      switch (type) {
        case FILTER_TYPES.SEARCH:
          return acc;
        default: {
          if (
            _.isEqual(currentFilters[type], defaultFilters[type]) ||
            !FILTER_TYPES[type]
          ) {
            return acc;
          } else {
            return acc + 1;
          }
        }
      }
    },
    0
  );
};

export const getAppliedFiltersCountForFilter = ({ filterKey, filterValue }) => {
  const defaultFilters = DEFAULT_FILTERS;
  const defaultFilterValue = defaultFilters[filterKey];

  if (_.isEqual(filterValue, defaultFilterValue)) {
    return 0;
  }

  return _.isArray(filterValue) ? _.size(filterValue) : 1;
};

// Returns the column which will acquire the allocated width for the data grid
// By default returns TITLE, for MYP related concept returns DESCRIPTION
export const getFillColumn = ({ plannerElementType }) => {
  if (plannerElementType === ELEMENT_TYPE_MYP_RELATED_CONCEPT) {
    return DATA_GRID_COLUMN_TYPES.DESCRIPTION;
  }
  if (plannerElementType === ELEMENT_TYPE_MYP_SPECIFIED_CONCEPT) {
    return DATA_GRID_COLUMN_TYPES.SUBJECT_GROUP;
  }
  if (
    _.includes(
      [
        ELEMENT_TYPE_MYP_LEARNING_STANDARD,
        ELEMENT_TYPE_DP_LEARNING_STANDARD,
        ELEMENT_TYPE_UBD_LEARNING_STANDARD,
        ELEMENT_TYPE_PYP_LEARNING_STANDARD,
      ],
      plannerElementType
    )
  )
    return undefined;
  return DATA_GRID_COLUMN_TYPES.TITLE;
};

// Returns the appropriate width for the title column based on planner element type
const getTitleColumnWidth = ({ plannerElementType }) => {
  switch (plannerElementType) {
    case ELEMENT_TYPE_MYP_LEARNING_STANDARD:
    case ELEMENT_TYPE_DP_LEARNING_STANDARD:
    case ELEMENT_TYPE_UBD_LEARNING_STANDARD:
    case ELEMENT_TYPE_PYP_LEARNING_STANDARD:
      return 640;
    case ELEMENT_TYPE_MYP_RELATED_CONCEPT:
    case ELEMENT_TYPE_MYP_SPECIFIED_CONCEPT:
      return 210;
    default:
      return 480;
  }
};

const defaultProperty = {
  headerConfig: {
    tooltip: "",
  },
  cellConfig: {
    styles: {
      selectedColor: "white",
    },
  },
};

export const getColumnProperties = ({
  defaultColumns,
  properties,
  plannerElementType,
}) => {
  const titleWidth = getTitleColumnWidth({ plannerElementType });
  const defaultColumnsProperties = _.reduce(
    defaultColumns,
    (acc, columnId) => {
      acc[columnId] = defaultProperty;
      return acc;
    },
    {}
  );

  const isParentTaggingEnabled = isParentTaggingEnabledForType({
    plannerElementType,
  });
  const countPropertyPathSuffix = isParentTaggingEnabled
    ? "directCount"
    : "totalCount";

  return _.merge(
    {},
    defaultColumnsProperties,
    {
      [DATA_GRID_COLUMN_TYPES.TAGGED_IN_ASSIGNMENTS]: {
        customConfig: {
          property: `dataSetNodeCountWrapper.assessment.${countPropertyPathSuffix}`,
          actionType: ACTION_TYPES.OPEN_TAGGED_IN_ASSIGNMENTS_MODAL,
          actionProp: "onCellClick",
        },
      },
      [DATA_GRID_COLUMN_TYPES.TAGGED_IN_PORTFOLIO]: {
        customConfig: {
          property: `dataSetNodeCountWrapper.portfolio.${countPropertyPathSuffix}`,
          actionType: ACTION_TYPES.OPEN_TAGGED_IN_PORTFOLIO_MODAL,
          actionProp: "onCellClick",
        },
      },
      [DATA_GRID_COLUMN_TYPES.TAGGED_IN_UNITS]: {
        customConfig: {
          property: `dataSetNodeCountWrapper.unitPlan.${countPropertyPathSuffix}`,
          actionType: ACTION_TYPES.OPEN_TAGGED_IN_UNITS_MODAL,
          actionProp: "onCellClick",
        },
      },
      [DATA_GRID_COLUMN_TYPES.POWER_STANDARD]: {
        customConfig: {
          property: `dataSetNodeWrapper.properties.isPowerStandard`,
        },
      },
      [DATA_GRID_COLUMN_TYPES.WEIGHT]: {
        customConfig: {
          property: `dataSetNodeWrapper.properties.weight`,
        },
      },
      [DATA_GRID_COLUMN_TYPES.GRADE_LEVELS]: {
        customConfig: {
          property: `dataSetNodeWrapper.properties.grades`,
        },
      },
      [DATA_GRID_COLUMN_TYPES.SUBJECT_AREAS]: {
        customConfig: {
          property: `dataSetNodeWrapper.properties.subjects`,
        },
      },
      [DATA_GRID_COLUMN_TYPES.TAGS]: {
        customConfig: {
          property: `dataSetNodeWrapper.properties.tags`,
        },
      },
      [DATA_GRID_COLUMN_TYPES.ARCHIVE]: {
        customConfig: {
          property: `dataSetNodeWrapper.properties.isArchived`,
        },
      },
      [DATA_GRID_COLUMN_TYPES.SUBJECT_GROUP]: {
        customConfig: {
          property: `dataSetNodeWrapper.properties.subjectGroups`,
        },
      },
      [DATA_GRID_COLUMN_TYPES.TITLE]: {
        headerConfig: {
          width: titleWidth,
        },
      },
    },
    properties
  );
};

const getSelectedNodesAndTheirAncestors = ({ selected, nodeMap }) => {
  const selectedNodes = new Set();
  const visited = new Set();
  _.forEach(selected, rowId => {
    let node = nodeMap.get(rowId);
    while (node) {
      selectedNodes.add(node.rowId);
      visited.add(node.rowId);
      if (visited.has(node.parentId)) break;
      node = nodeMap.get(node.parentId);
    }
  });
  return _.toArray(selectedNodes);
};

const getCanNodeBeRemoved = ({ node }) => {
  const { isUntaggingDisabled = true, isLocked = true } = node?.metadata || {};
  return !(isUntaggingDisabled || isLocked);
};

const getRemovedNodesAndTheirDescendants = ({
  removed,
  nodeMap,
  selectedRows,
}) => {
  const removedNodes = new Set();
  const removedSet = new Set(removed);
  const addIdToRemovedDS = id => {
    removed.push(id);
    removedSet.add(id);
  };

  while (!_.isEmpty(removed)) {
    const rowId = removed.pop();
    let canBeRemoved = true;
    const node = nodeMap.get(rowId);
    const { allChildren, parentId } = node;
    //this checks if we can remove the parent when current node is the only selected child.
    if (parentId && !removedNodes.has(parentId)) {
      const parentNode = nodeMap.get(parentId);
      let canParentBeRemoved = getCanNodeBeRemoved({ node: parentNode });
      if (canParentBeRemoved) {
        const { allChildren } = parentNode;
        canParentBeRemoved = _.every(allChildren, childId => {
          const check =
            !selectedRows.has(childId) ||
            removedNodes.has(childId) ||
            removedSet.has(childId);
          return check;
        });
      }
      if (canParentBeRemoved) addIdToRemovedDS(parentId);
    }
    //this checks all descendants which can be removed
    _.forEach(allChildren, childId => {
      const childNode = nodeMap.get(childId) || {};
      //keeping by default true, as we don't want to unselect those nodes which are not visible,
      //and also preventing the parent from being deselected.
      if (selectedRows.has(childId)) {
        if (getCanNodeBeRemoved({ node: childNode })) addIdToRemovedDS(childId);
        else canBeRemoved = false;
      }
    });
    if (canBeRemoved) removedNodes.add(rowId);
    else removedSet.delete(rowId);
  }
  return _.toArray(removedNodes);
};

export const handleSelectionChange = ({
  params = [],
  selectedRows,
  updateSelectedRows,
  nodeMap,
  config,
}) => {
  let selected = [];
  let removed = [];
  const { moduleType } = config;

  _.forEach(params, ({ cellCoods, newValue }) => {
    const { rowId } = cellCoods;
    const isChecked = newValue?.value === DATA_GRID_CHECKBOX_STATES.CHECKED;

    if (isChecked) {
      selected.push(rowId);
    } else {
      removed.push(rowId);
    }
  });
  switch (moduleType) {
    case "LearningCourse":
      selected = getSelectedNodesAndTheirAncestors({
        selected,
        nodeMap,
      });
      removed = getRemovedNodesAndTheirDescendants({
        removed,
        nodeMap,
        selectedRows,
      });
  }

  const newSelectedRows = new Set(selectedRows);
  if (_.size(selected) > 0 || _.size(removed) > 0) {
    removed.forEach(rowId => newSelectedRows.delete(rowId));
    selected.forEach(rowId => newSelectedRows.add(rowId));
    updateSelectedRows(newSelectedRows);
  }
  return { selected, removed, newSelectedRows };
};

const getLabelLowercase = ({ label, count, t }) => {
  return t("common:label_lowercase", {
    label: t(label, { count }),
  });
};

const getCount = entity => ({
  total: _.get(entity, "totalCount", 0),
  direct: _.get(entity, "directCount"),
});

export const getMetadata = ({
  node,
  parentId,
  nodeMap,
  selectedRows,
  config,
  hasTaggedInData,
  isParentTaggingEnabled,
}) => {
  const { isArchived = false } = node?.dataSetNodeWrapper?.properties || {};
  const { assessment, portfolio, unitPlan } =
    node?.dataSetNodeCountWrapper || {};
  const parentUID = parentId ? nodeMap.get(parentId)?.uid : "-";

  const { isLocked = false } = node?.lockingInfo || {};

  const assessmentCount = getCount(assessment);
  const portfolioCount = getCount(portfolio);
  const unitPlanCount = getCount(unitPlan);
  const children = node?.children || [];
  const { moduleType, disableUntaggingIfTaggedInOtherEntities = true } =
    config || {};

  const isTaggedDataAvailable =
    !hasTaggedInData ||
    (assessment && portfolio && unitPlan) ||
    !disableUntaggingIfTaggedInOtherEntities;

  const isSelected = selectedRows.has(node?.id);
  let isTaggingDisabled = isArchived;
  const isUntaggingDisabled =
    (!!(assessmentCount.total || unitPlanCount.total || portfolioCount.total) &&
      disableUntaggingIfTaggedInOtherEntities) ||
    !isTaggedDataAvailable;
  switch (moduleType) {
    case "UnitPlan":
      isTaggingDisabled =
        isTaggingDisabled || (!isParentTaggingEnabled && !_.isEmpty(children));
      break;
    case "UnitPlanV2":
      isTaggingDisabled =
        isTaggingDisabled ||
        (!isStandardsParentTaggingEnabled() && !_.isEmpty(children));
      break;
    default:
      break;
  }
  const isCheckboxDisabled = isSelected
    ? isUntaggingDisabled
    : isTaggingDisabled;
  return {
    isArchived,
    parentUID,
    isTaggedDataAvailable,
    portfolioCount,
    unitPlanCount,
    assessmentCount,
    isCheckboxDisabled,
    isTaggingDisabled,
    isUntaggingDisabled,
    isParentNode: !_.isEmpty(children),
    isLocked: selectedRows.has(node.id) && isLocked,
  };
};

const getDisabledTooltip = ({
  metadata,
  hasHiddenTaggedChildren,
  t,
  plannerElementType,
}) => {
  const {
    portfolioCount,
    unitPlanCount,
    assessmentCount,
    isTaggedDataAvailable,
  } = metadata || {};

  if (!isTaggedDataAvailable) return "";

  // Use directCount when parent tagging is enabled, otherwise fallback to totalCount
  const hasDirectCounts = isParentTaggingEnabledForType({
    plannerElementType,
  });
  const displayAssessmentCount = hasDirectCounts
    ? assessmentCount.direct
    : assessmentCount.total;
  const displayUnitPlanCount = hasDirectCounts
    ? unitPlanCount.direct
    : unitPlanCount.total;
  const displayPortfolioCount = hasDirectCounts
    ? portfolioCount.direct
    : portfolioCount.total;

  const assessmentText = displayAssessmentCount
    ? t("common:label_count", {
        count: displayAssessmentCount,
        label: getLabelLowercase({
          t,
          label: "common:task",
          count: displayAssessmentCount,
        }),
      })
    : "";

  const unitPlanText = displayUnitPlanCount
    ? t("common:label_count", {
        count: displayUnitPlanCount,
        label: getLabelLowercase({
          t,
          label: "common:unit",
          count: displayUnitPlanCount,
        }),
      })
    : "";

  const portfolioText = displayPortfolioCount
    ? t("common:label_count", {
        count: displayPortfolioCount,
        label: getLabelLowercase({
          t,
          label: "common:portfolio",
          count: displayPortfolioCount,
        }),
      })
    : "";

  const texts = [unitPlanText, assessmentText, portfolioText].filter(Boolean);

  const usedIn =
    texts.length === 1
      ? texts[0]
      : texts.slice(0, -1).join(", ") +
        ` ${t("common:and")} ` +
        texts.slice(-1);

  if (texts.length > 0)
    return t("scopeAndSequence:standards_used_in_disabled_tooltip", {
      usedIn,
    });

  // directCounts are all 0 but totalCounts are non-zero → sub-standards are tagged
  if (
    hasDirectCounts &&
    (assessmentCount.total || unitPlanCount.total || portfolioCount.total)
  ) {
    return t(
      "scopeAndSequence:standard_cannot_be_deselected_sub_standards_in_use"
    );
  }

  if (hasHiddenTaggedChildren)
    return t("scopeAndSequence:hidden_tagged_children_tooltip");
};

export const getEdgesFromNodeMap = ({
  nodeMap,
  selectedRows,
  selectionColIndex,
  config,
  t,
  plannerElementType,
}) =>
  Array.from(
    nodeMap.values(),
    ({ rowId, columns, children, metadata, allChildren }) => {
      const { moduleType } = config || {};
      if (selectionColIndex !== -1) {
        const isSelected = selectedRows.has(rowId);
        columns[selectionColIndex].value = getCheckboxValue({
          value: isSelected,
        });
        let { isCheckboxDisabled } = metadata || {};
        let hasHiddenTaggedChildren = false;
        columns[selectionColIndex].tooltip = "";

        switch (moduleType) {
          case "LearningCourse": {
            const { lockedStandardTooltip } = config;
            const { isLocked } = metadata || {};
            if (isLocked) {
              columns[selectionColIndex].cellType = DATA_GRID_CELL_TYPES.ICON;
              columns[selectionColIndex].value = "LockFilled";
              columns[selectionColIndex].tooltip = t(lockedStandardTooltip);
            }
            hasHiddenTaggedChildren = _.some(
              allChildren,
              childId =>
                !_.includes(children, childId) && selectedRows.has(childId)
            );
            isCheckboxDisabled ||= hasHiddenTaggedChildren;
            if (isCheckboxDisabled) {
              columns[selectionColIndex].tooltip = getDisabledTooltip({
                metadata,
                hasHiddenTaggedChildren,
                t,
                plannerElementType,
              });
            }
          }
        }
        columns[selectionColIndex].isDisabled = isCheckboxDisabled;
      }
      return {
        rowId,
        columns,
        children,
      };
    }
  );

export const getNonConfigurableColumns = ({
  plannerElementType,
  defaultColumns,
}) => {
  const isStandards = getIsStandards({ plannerElementType });
  let nonConfigurableColumns = {
    start: [
      DATA_GRID_COLUMN_TYPES.SELECTION,
      DATA_GRID_COLUMN_TYPES.TITLE,
      DATA_GRID_COLUMN_TYPES.SUBJECT_GROUP,
      DATA_GRID_COLUMN_TYPES.CODE,
    ],
  };
  if (isStandards) {
    nonConfigurableColumns = {
      start: [
        DATA_GRID_COLUMN_TYPES.SELECTION,
        DATA_GRID_COLUMN_TYPES.CODE,
        DATA_GRID_COLUMN_TYPES.TITLE,
      ],
    };
  }
  switch (plannerElementType) {
    case ELEMENT_TYPE_MYP_RELATED_CONCEPT:
      nonConfigurableColumns.start.push(DATA_GRID_COLUMN_TYPES.DESCRIPTION);
      break;
  }
  const allowedStartColumns = _.intersection(
    nonConfigurableColumns.start,
    defaultColumns
  );
  return {
    start: allowedStartColumns,
  };
};
const getCurrentColumnsForStandardsV1 = ({ plannerElementType }) => {
  const columnIdsToDisplay = [
    DATA_GRID_COLUMN_TYPES.SELECTION,
    DATA_GRID_COLUMN_TYPES.TITLE,
  ];
  switch (plannerElementType) {
    case ELEMENT_TYPE_MYP_LEARNING_STANDARD:
    case ELEMENT_TYPE_DP_LEARNING_STANDARD:
    case ELEMENT_TYPE_UBD_LEARNING_STANDARD:
      columnIdsToDisplay.push(DATA_GRID_COLUMN_TYPES.DESCRIPTION);
      columnIdsToDisplay.push(DATA_GRID_COLUMN_TYPES.CODE);
      columnIdsToDisplay.push(DATA_GRID_COLUMN_TYPES.GRADE_LEVELS);
      columnIdsToDisplay.push(DATA_GRID_COLUMN_TYPES.TAGS);
      break;
    case ELEMENT_TYPE_PYP_LEARNING_STANDARD:
      columnIdsToDisplay.push(DATA_GRID_COLUMN_TYPES.DESCRIPTION);
      columnIdsToDisplay.push(DATA_GRID_COLUMN_TYPES.CODE);
      columnIdsToDisplay.push(DATA_GRID_COLUMN_TYPES.GRADE_LEVELS);
      columnIdsToDisplay.push(DATA_GRID_COLUMN_TYPES.TAGS);
      columnIdsToDisplay.push(DATA_GRID_COLUMN_TYPES.STAAR_QUES);
      break;
  }
  return columnIdsToDisplay;
};

const getCurrentColumnsForStandards = ({
  academicSetupType,
  plannerElementType,
  moduleType,
}) => {
  const isOldSubjectBasedGrading = academicSetupType === null;
  const isCourseBasedGrading =
    academicSetupType === ACADEMIC_SETUP_TYPES.COURSE_BASED_GRADING;
  switch (moduleType) {
    case "UnitPlan":
      return getCurrentColumnsForStandardsV1({ plannerElementType });
    default: {
      let columnIdsToDisplay = [
        DATA_GRID_COLUMN_TYPES.SELECTION,
        DATA_GRID_COLUMN_TYPES.CODE,
        DATA_GRID_COLUMN_TYPES.TITLE,
        DATA_GRID_COLUMN_TYPES.DESCRIPTION,
        DATA_GRID_COLUMN_TYPES.GRADE_LEVELS,
        DATA_GRID_COLUMN_TYPES.TAGS,
      ];
      if (!isOldSubjectBasedGrading) {
        columnIdsToDisplay = [
          DATA_GRID_COLUMN_TYPES.SELECTION,
          DATA_GRID_COLUMN_TYPES.CODE,
          DATA_GRID_COLUMN_TYPES.TITLE,
          DATA_GRID_COLUMN_TYPES.POWER_STANDARD,
          DATA_GRID_COLUMN_TYPES.GRADE_LEVELS,
          DATA_GRID_COLUMN_TYPES.GRADING_TYPE,
          DATA_GRID_COLUMN_TYPES.TAGS,
          DATA_GRID_COLUMN_TYPES.DESCRIPTION,
          DATA_GRID_COLUMN_TYPES.ID,
          DATA_GRID_COLUMN_TYPES.LIST_PARENT,
          DATA_GRID_COLUMN_TYPES.ARCHIVE,
        ];
      } else if (isCourseBasedGrading) {
        columnIdsToDisplay = [
          DATA_GRID_COLUMN_TYPES.SELECTION,
          DATA_GRID_COLUMN_TYPES.CODE,
          DATA_GRID_COLUMN_TYPES.TITLE,
          DATA_GRID_COLUMN_TYPES.POWER_STANDARD,
          DATA_GRID_COLUMN_TYPES.GRADE_LEVELS,
          DATA_GRID_COLUMN_TYPES.GRADING_TYPE,
          DATA_GRID_COLUMN_TYPES.SUBJECT_AREAS,
          DATA_GRID_COLUMN_TYPES.TAGS,
          DATA_GRID_COLUMN_TYPES.DESCRIPTION,
          DATA_GRID_COLUMN_TYPES.ID,
          DATA_GRID_COLUMN_TYPES.LIST_PARENT,
          DATA_GRID_COLUMN_TYPES.ARCHIVE,
        ];
      }
      if (
        plannerElementType === ELEMENT_TYPE_PYP_LEARNING_STANDARD &&
        ACLStore.can("TeacherPortal:PlannerBenchmarkQuestions")
      ) {
        columnIdsToDisplay.push(DATA_GRID_COLUMN_TYPES.STAAR_QUES);
      }
      return columnIdsToDisplay;
    }
  }
};

const getIsStandards = ({ plannerElementType }) => {
  return _.includes(
    [
      ELEMENT_TYPE_MYP_LEARNING_STANDARD,
      ELEMENT_TYPE_DP_LEARNING_STANDARD,
      ELEMENT_TYPE_UBD_LEARNING_STANDARD,
      ELEMENT_TYPE_PYP_LEARNING_STANDARD,
    ],
    plannerElementType
  );
};

const getIsATLs = ({ plannerElementType }) => {
  return _.includes(
    [
      ELEMENT_TYPE_MYP_ATL,
      ELEMENT_TYPE_DP_ATL,
      ELEMENT_TYPE_UBD_ATL,
      ELEMENT_TYPE_PYP_ATL,
    ],
    plannerElementType
  );
};

export const isParentTaggingEnabledForType = ({
  plannerElementType,
  uiType,
}) => {
  if (getIsStandards({ plannerElementType })) {
    return isStandardsParentTaggingEnabled();
  }
  if (getIsATLs({ plannerElementType })) return isATLsParentTaggingEnabled();
  return (
    plannerElementType === PLANNER_ELEMENT_NESTED_CHECKLIST &&
    isDatasetsParentTaggingEnabled()
  );
};

export const isParentTaggingSupportedForPlannerElementType = ({
  plannerElementType,
}) => {
  return PARENT_TAGGING_SUPPORTED_PLANNER_ELEMENT_TYPES.has(plannerElementType);
};

const STANDARDS_FIELD_UIDS = [
  "learningStandardMYP",
  "learningStandardUBD",
  "disciplinaryLearningStandardMYP",
  "benchmarks",
  "subjectStandardDP",
];

const ATL_FIELD_UIDS = ["atls", "atlsMYP", "atlsUBD", "atlDP"];

export const shouldShowFlatListForType = ({ plannerElementType, fieldUID }) => {
  const config = { isStandards: false, showFlatList: false };
  if (plannerElementType) {
    if (getIsStandards({ plannerElementType })) {
      config.isStandards = true;
      config.showFlatList = shouldShowFlatListForStandards();
    } else if (
      PARENT_TAGGING_SUPPORTED_PLANNER_ELEMENT_TYPES.has(plannerElementType)
    ) {
      config.showFlatList = shouldShowFlatListForAtls();
    }
  }
  if (fieldUID) {
    if (_.includes(STANDARDS_FIELD_UIDS, fieldUID)) {
      config.isStandards = true;
      config.showFlatList = shouldShowFlatListForStandards();
    }
    if (_.includes(ATL_FIELD_UIDS, fieldUID)) {
      config.showFlatList = shouldShowFlatListForAtls();
    }
  }
  return config;
};

export const getCurrentColumns = ({
  academicSetupType,
  allowedColumns,
  plannerElementType,
  moduleType,
}) => {
  const isStandards = getIsStandards({ plannerElementType });
  if (isStandards)
    return getCurrentColumnsForStandards({
      academicSetupType,
      plannerElementType,
      moduleType,
    });

  const columnIdsToDisplay = [
    DATA_GRID_COLUMN_TYPES.SELECTION,
    DATA_GRID_COLUMN_TYPES.TITLE,
  ];
  _.forEach(allowedColumns, column => {
    switch (column) {
      case "allowCodes":
        columnIdsToDisplay.push(DATA_GRID_COLUMN_TYPES.CODE);
        break;
      case "allowGrades":
        columnIdsToDisplay.push(DATA_GRID_COLUMN_TYPES.GRADE_LEVELS);
        break;
      case "allowTags":
        columnIdsToDisplay.push(DATA_GRID_COLUMN_TYPES.TAGS);
        break;
    }
  });

  switch (plannerElementType) {
    case ELEMENT_TYPE_DP_SYLLABUS:
      columnIdsToDisplay.push(DATA_GRID_COLUMN_TYPES.CODE);
      columnIdsToDisplay.push(DATA_GRID_COLUMN_TYPES.TAGS);
      break;
    case ELEMENT_TYPE_MYP_RELATED_CONCEPT:
      columnIdsToDisplay.push(DATA_GRID_COLUMN_TYPES.DESCRIPTION);
      break;
    case ELEMENT_TYPE_PYP_ATL:
      columnIdsToDisplay.push(DATA_GRID_COLUMN_TYPES.GRADE_LEVELS);
      break;
    case ELEMENT_TYPE_MYP_ATL:
    case ELEMENT_TYPE_UBD_ATL:
    case ELEMENT_TYPE_UBD_SKILL:
    case ELEMENT_TYPE_DP_ATL:
      columnIdsToDisplay.push(DATA_GRID_COLUMN_TYPES.CODE);
      columnIdsToDisplay.push(DATA_GRID_COLUMN_TYPES.GRADE_LEVELS);
      break;
    case ELEMENT_TYPE_MYP_SPECIFIED_CONCEPT:
      columnIdsToDisplay.push(DATA_GRID_COLUMN_TYPES.SUBJECT_GROUP);
  }
  return columnIdsToDisplay;
};

export const getFilterStateFromVariable = ({ filters }) => {
  const filterState = {};
  _.forEach(_.entries(filters), ([variableName, value]) => {
    const filterType = VARIABLE_NAME_TO_FILTER_TYPE[variableName];
    if (filterType) filterState[filterType] = value;
  });
  return filterState;
};

export const getExtraFilters = ({
  sideNavigationTab,
  parentNodesData,
  plannerElementType,
}) => {
  const entityType = _.get(parentNodesData, "entityType");
  const {
    shouldUseParentNodes,
    hasMultipleNodeSet,
  } = getPlannerElementConfigurations({
    plannerElementType,
  });
  if (sideNavigationTab && !hasMultipleNodeSet) {
    if (entityType && shouldUseParentNodes)
      return { [entityType]: [sideNavigationTab] };
    return { parentNodes: [sideNavigationTab] };
  }
};

export const getNodeSetFilters = ({
  sideNavigationTab,
  parentNodesData,
  plannerElementType,
}) => {
  const entityType = _.get(parentNodesData, "entityType");
  const {
    shouldUseParentNodes,
    hasMultipleNodeSet,
  } = getPlannerElementConfigurations({
    plannerElementType,
  });
  if (sideNavigationTab && hasMultipleNodeSet) {
    if (entityType && shouldUseParentNodes)
      return { [entityType]: [sideNavigationTab] };
    return { parentNodes: [sideNavigationTab] };
  }
};

export const getPlannerElementConfigurations = ({ plannerElementType }) => {
  const config = {
    shouldUseParentNodes: false,
    showSideNavigation: false,
    subTypeRootDepth: 2,
    allowedLocalFilters: ["searchText", "parentNodes"],
    canTagFromAllGrades: false,
    hasMultipleNodeSet: false,
    filtersToBeShown: [],
  };
  switch (plannerElementType) {
    case ELEMENT_TYPE_DP_SYLLABUS:
      config.showSideNavigation = false;
      break;
    case ELEMENT_TYPE_MYP_RELATED_CONCEPT:
      config.shouldUseParentNodes = true;
      config.useLocalFiltering = true;
      config.allowedLocalFilters = ["searchText"];
      config.showSideNavigation = true;
      break;
    case ELEMENT_TYPE_MYP_OBJECTIVES:
      config.useLocalFiltering = true;
      config.showSideNavigation = true;
      break;
    case ELEMENT_TYPE_MYP_SPECIFIED_CONCEPT:
    case ELEMENT_TYPE_DP_KNOWLEDGE_FRAMEWORK: {
      config.useLocalFiltering = true;
      config.showSideNavigation = false;
      break;
    }
    case ELEMENT_TYPE_PYP_LEARNING_STANDARD:
    case ELEMENT_TYPE_MYP_LEARNING_STANDARD:
    case ELEMENT_TYPE_DP_LEARNING_STANDARD:
    case ELEMENT_TYPE_UBD_LEARNING_STANDARD:
      config.hasMultipleNodeSet = true;
      config.shouldUseParentNodes = !ACLStore.can(ENABLE_STANDARDS_V2);
      config.showSideNavigation = !ACLStore.can(ENABLE_STANDARDS_V2);
      config.canTagFromAllGrades = true;
      config.filtersToBeShown = [
        { type: FILTER_TYPES.STATUS, permission: ENABLE_STANDARDS_V2 },
      ];
      break;
    case ELEMENT_TYPE_PYP_ATL:
    case ELEMENT_TYPE_MYP_ATL:
    case ELEMENT_TYPE_DP_ATL:
    case ELEMENT_TYPE_UBD_SKILL:
    case ELEMENT_TYPE_UBD_ATL: {
      config.showSideNavigation = true;
      config.filtersToBeShown = [
        { type: FILTER_TYPES.STATUS, permission: DATASET_V2_PERMISSION },
      ];
      break;
    }
  }
  return config;
};

const setRootNodes = ({ ancestors, rootNodeId, nodeToRootNodeIdMap }) => {
  _.forEach(ancestors, id => (nodeToRootNodeIdMap[id] = rootNodeId));
};

const getRootNodeId = ({ idNodeMap, node, nodeToRootNodeIdMap }) => {
  let current = node;
  const ancestors = [node.id];
  while (current.parent) {
    if (nodeToRootNodeIdMap[current.id]) {
      const rootNodeId = nodeToRootNodeIdMap[current.id];
      setRootNodes({ ancestors, rootNodeId, nodeToRootNodeIdMap });
      return rootNodeId;
    }
    const { parent: parentId } = current;
    if (!idNodeMap[parentId]) break;
    current = idNodeMap[parentId].node;
    ancestors.push(parentId);
  }
  const rootNodeId = current.id;
  setRootNodes({ ancestors, rootNodeId, nodeToRootNodeIdMap });
  return rootNodeId;
};

export const getRootNodesToChildrenMap = ({ edges }) => {
  const rootNodesToChildrenMap = {};
  const nodeToRootNodeIdMap = {};
  const idNodeMap = _.keyBy(edges, "node.id");
  _.forEach(edges, edge => {
    const { node } = edge;
    const rootNodeId = getRootNodeId({ idNodeMap, node, nodeToRootNodeIdMap });
    if (rootNodeId) {
      if (rootNodesToChildrenMap[rootNodeId]) {
        rootNodesToChildrenMap[rootNodeId].push(edge);
      } else {
        rootNodesToChildrenMap[rootNodeId] = [edge];
      }
    }
  });
  return rootNodesToChildrenMap;
};

// Unified filter function that applies all filters using switch case
const applyFilterToNode = (node, filterType, filterData) => {
  switch (filterType) {
    case "SEARCH": {
      const { lowerCaseSearchText } = filterData;
      const nodeLabel = _.toLower(node.label || "");
      const nodeCode = _.toLower(node.code || "");

      return (
        nodeLabel.includes(lowerCaseSearchText) ||
        nodeCode.includes(lowerCaseSearchText)
      );
    }

    default:
      return true;
  }
};

// Helper function to find all descendants of a node
const findAllDescendants = (nodeId, nodesByIdMap, descendantsSet) => {
  const node = nodesByIdMap[nodeId] || {};
  if (!node) return;

  descendantsSet.add(nodeId);
  // Find all children nodes and recursively get their descendants
  _.forEach(node.children, childNodeId => {
    if (!descendantsSet.has(childNodeId)) {
      findAllDescendants(childNodeId, nodesByIdMap, descendantsSet);
    }
  });
};

// Helper function to find all ancestors of a node
const findAllAncestors = (nodeId, nodesByIdMap, ancestorsSet) => {
  const node = nodesByIdMap[nodeId] || {};
  if (!node) return;

  ancestorsSet.add(nodeId);

  const parentId = node.parent;
  if (parentId && nodesByIdMap[parentId] && !ancestorsSet.has(parentId)) {
    findAllAncestors(parentId, nodesByIdMap, ancestorsSet);
  }
};

// Helper function to apply search filter and get descendants
const applySearchFilterWithDescendants = (nodes, nodesByIdMap, searchText) => {
  const searchMatchedNodeIds = new Set();
  const lowerCaseSearchText = _.toLower(searchText);

  _.forEach(nodes, node => {
    const passesSearchFilter = applyFilterToNode(node, "SEARCH", {
      lowerCaseSearchText,
    });

    if (passesSearchFilter) {
      searchMatchedNodeIds.add(node.id);
    }
  });

  // Find all descendants of search-matched nodes
  const searchAndDescendantsSet = new Set();
  searchMatchedNodeIds.forEach(nodeId => {
    findAllDescendants(nodeId, nodesByIdMap, searchAndDescendantsSet);
  });

  return searchAndDescendantsSet;
};

// Optimized filtering function with new flow: search -> descendants -> other filters -> ancestors
const getFilteredNodesWithAllFilters = ({ nodes, filters = {} }) => {
  const { searchText = "" } = filters || {};
  let eligibleNodeIds = new Set(_.map(nodes, "id"));

  if (_.isEmpty(searchText)) {
    return eligibleNodeIds;
  }

  const nodesByIdMap = _.keyBy(nodes, "id");

  // Step 1: Apply search filter first if present and get descendants
  if (!_.isEmpty(searchText)) {
    eligibleNodeIds = applySearchFilterWithDescendants(
      nodes,
      nodesByIdMap,
      searchText
    );
  }

  // Step 3: Find all ancestors of the final filtered nodes
  const finalNodeIds = new Set();
  eligibleNodeIds.forEach(nodeId => {
    findAllAncestors(nodeId, nodesByIdMap, finalNodeIds);
  });

  return finalNodeIds;
};

export const getFilteredEdges = ({
  edges,
  rootNodesToChildrenMap,
  nodesFilters,
}) => {
  const { parentNodes } = nodesFilters;
  const applicableEdges = _.isEmpty(parentNodes)
    ? edges
    : rootNodesToChildrenMap[_.first(parentNodes)];
  const nodes = _.map(applicableEdges, "node");
  const finalNodeIds = getFilteredNodesWithAllFilters({
    nodes,
    filters: nodesFilters,
  });

  return _.filter(applicableEdges, ({ node }) => finalNodeIds.has(node.id));
};

export const getShouldShowNewTaggingModal = ({ isDynamicField = false }) => {
  return ACLStore.can(ENABLE_NEW_TAGGING_MODAL) && !isDynamicField;
};

export const getResolvedMinimalTreeSet = ({
  resolvedMinimalTree,
  plannerElementType,
}) => {
  switch (plannerElementType) {
    case ELEMENT_TYPE_PYP_LEARNING_STANDARD:
      return _.get(resolvedMinimalTree, "benchmarks", []);
    case ELEMENT_TYPE_PYP_ATL:
      return _.get(resolvedMinimalTree, "atls", []);
    default:
      return _.get(resolvedMinimalTree, "nodes", []);
  }
};
